import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * BrandingContext
 *
 * Fetches /branding/login (PUBLIC endpoint — no auth required) once on mount and
 * exposes the config to the whole app. The login page renders before authentication,
 * so this MUST NOT depend on the auth cookie being present.
 *
 * `refresh()` is exposed so the Settings > Branding editor can push updates and see
 * them reflected everywhere without a page reload.
 */
const DEFAULT_BRANDING = {
  brand_name: "VELACIN",
  brand_tagline: "HR × PM Suite",
  brand_mark_color: "#002FA7",
  logo_url: "",
  eyebrow: "01 / Access",
  headline: "Sign in to\nthe workspace.",
  subcopy: "Manage attendance, leave, teams, and projects — one crisp control room for the entire company.",
  submit_label: "Sign in",
  footer_text: "© 2026 Velacin Systems Inc.",
  show_right_panel: true,
  right_image_url: "https://images.pexels.com/photos/16370914/pexels-photo-16370914.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1200",
  right_eyebrow: "Trusted by teams",
  right_headline: "Move faster with a workspace built for both people & projects.",
  right_overlay_color: "#002FA7",
};

const BrandingContext = createContext(null);

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/branding/login");
      setBranding({ ...DEFAULT_BRANDING, ...r.data });
    } catch (e) {
      // Silently keep defaults — the login page must still render.
      // eslint-disable-next-line no-console
      console.warn("Failed to fetch branding, using defaults:", e?.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <BrandingContext.Provider value={{ branding, setBranding, refresh, loaded }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) return { branding: DEFAULT_BRANDING, setBranding: () => {}, refresh: () => {}, loaded: true };
  return ctx;
}
